// 效果评估表格 custom 单元格组件 + KPI 选项 hook。
// customComp 受控范式（value/onChange）镜像 SolutionTable duration_value / DurationInput；
// 只读态（editable=false）纯文本展示，编辑态渲染对应控件。

import React, { useEffect, useMemo, useState } from 'react';
import { Input, InputNumber, Select, Tooltip } from '@bedrock/components';
import { HelpCircle } from '@bedrock/icons-react';
import {
  v2IpCooperationDemandL1EffectIndicators,
  type IpCooperationEffectKpiConfig,
} from '#services/api';


// ============================================================
// 效果指标下拉选项获取（v2IpCooperationDemandL1EffectIndicators，GET 无参）
// ============================================================
let kpiOptionsCache: IpCooperationEffectKpiConfig[] | null = null;
let kpiOptionsPending: Promise<IpCooperationEffectKpiConfig[]> | null = null;

const fetchKpiOptions = (): Promise<IpCooperationEffectKpiConfig[]> => {
  if (kpiOptionsCache) return Promise.resolve(kpiOptionsCache);
  if (!kpiOptionsPending) {
    kpiOptionsPending = v2IpCooperationDemandL1EffectIndicators({
      silent: true,
    })
      .then((res) => {
        kpiOptionsCache = res?.data ?? [];
        return kpiOptionsCache;
      })
      .finally(() => {
        kpiOptionsPending = null;
      });
  }
  return kpiOptionsPending;
};

/** 效果指标下拉选项（模块级缓存） */
export const useKpiOptions = (): IpCooperationEffectKpiConfig[] => {
  const [options, setOptions] = useState<IpCooperationEffectKpiConfig[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchKpiOptions()
      .then((list) => {
        if (!cancelled)
          setOptions(list.map((item) => ({ ...item, value: item.code })));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return options;
};

// ============================================================
// 效果指标：下拉选择（value=kpi_code，label=kpi name）+ 帮助提示；只读态显示指标名
// ============================================================
export const EffectMetricSelect = ({
  value,
  onChange,
  editable,
  options = [],
  row,
}: {
  value?: string;
  onChange?: (v?: string) => void;
  editable?: boolean;
  options?: IpCooperationEffectKpiConfig[];
  row?: any;
}) => {
  const metric = useMemo(
    () => (options || []).find((o) => o.code === value),
    [options, value]
  );

  const finalOptions = useMemo(
    () => (options || []).map((o) => ({ name: o.name, value: o.code })),
    [options]
  );

  if (!editable) {
    return <span>{row?.kpi_schema?.name || '--'}</span>;
  }
  return (
    <div className="flex items-center gap-1">
      <Select
        value={value}
        onChange={(v) => {
          onChange?.(v);
        }}
        options={finalOptions}
        placeholder="请选择指标"
        showSearch
        allowClear
        style={{ flex: 1 }}
      />
      {Boolean(metric?.description) && (
        <Tooltip title={metric?.description}>
          <HelpCircle className="text-color-3" size={14} />
        </Tooltip>
      )}
    </div>
  );
};

// ============================================================
// 效果预期：按所选指标类型渲染控件（numeric → InputNumber，plain_text → Input）
// row.kpi_code 读取当前子行所选指标（editRender 经 props.row 注入）
// ============================================================
export const ExpectValueInput = ({
  value,
  onChange,
  editable,
  row,
  options = [],
}: {
  value?: string;
  onChange?: (v?: string) => void;
  editable?: boolean;
  row?: any;
  options?: IpCooperationEffectKpiConfig[];
}) => {
  const metric = (options || []).find((o) => o.code === row?.kpi_code);
  // 数值型指标（numeric）→ InputNumber；文本型指标（plain_text）→ Input（按 type）
  // percent 指标（percent=1）在 InputNumber 后展示 % 后缀（对齐设计稿 90% 形态）
  const isNumeric = metric?.type === 'numeric';
  const isPercent = metric?.percent === 1;
  if (!editable) {
    // 只读态：percent 指标拼 % 后缀（90 → 90%，对齐设计稿 90% 形态）；空值回退 --（0 为合法值须展示，避免 --%）
    if (value == null || value === '') return <span>--</span>;
    return <span>{isPercent ? `${value}%` : value}</span>;
  }
  if (isNumeric) {
    return (
      <InputNumber
        value={value as any}
        onChange={(v) => onChange?.(v as any)}
        min={0}
        precision={2}
        direction={'vertical'}
        placeholder="请输入"
        addonAfter={isPercent ? '%' : undefined}
        addonBlock={true}
        style={{ width: '100%' }}
      />
    );
  }
  return (
    <Input
      value={value}
      onChange={(e) => onChange?.(e?.target?.value)}
      placeholder="请输入"
    />
  );
};

